/**
 * 3D Cursor operations — snap cursor to elements and elements to cursor.
 *
 * Every function is a pure state transform: it takes a read slice of the
 * app state and returns a partial state patch. The store adapter wraps
 * each call with `pushUndo()` and merges the patch in. Keeping the logic
 * pure means undo/redo, persistence, and tests all work without pulling
 * in Zustand or React.
 *
 * The cursor lives in world space. Edit-mode snap targets work in
 * object-local space — conversions happen here so call sites stay
 * oblivious.
 *
 * Mirrors Blender's Shift+S pie menu while fixing its rough edges
 * (selection / touch / DRY across collapsed and expanded sidebars).
 */

import * as THREE from "three/webgpu";
import {
  composeGroupTransform,
  decomposeWorldToLocalTransform,
} from "../app/groupTransform.ts";
import type { GroupTransform } from "../app/storeTypes.ts";
import type { Cursor3DState } from "./cursor3d.ts";
import {
  hasEdgeSelection,
  hasFaceSelection,
  isFaceMode,
  vertexCentroid,
} from "./editSelection.ts";
import { recomputeLoopNormals, resolveAutoSmoothAngle } from "./meshOps.ts";
import { topologyFaceVerts } from "./topology.ts";
import type { EditModeState, PolygonTopology, SceneObject } from "./types.ts";
import { asLoopNormals } from "./types.ts";

// ---------------------------------------------------------------------------
// World ↔ local helpers
// ---------------------------------------------------------------------------

const _m = new THREE.Matrix4();
const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();

/** Compose an object's TRS matrix without allocating. */
function objectMatrix(obj: SceneObject, out: THREE.Matrix4): THREE.Matrix4 {
  const [px, py, pz] = obj.position;
  const [rx, ry, rz] = obj.rotation;
  const [sx, sy, sz] = obj.scale;
  return out.compose(
    _v.set(px, py, pz),
    _q.setFromEuler(_e.set(rx, ry, rz)),
    new THREE.Vector3(sx, sy, sz),
  );
}

/**
 * Convert a world-space point to `obj`'s local space. Used when applying
 * cursor-based operations to edit-mode vertex data.
 */
export function worldToObjectLocal(
  obj: SceneObject,
  worldPoint: readonly [number, number, number],
  parentTransform?: GroupTransform | null,
): [number, number, number] {
  if (parentTransform) {
    const local = decomposeWorldToLocalTransform(parentTransform, {
      position: [worldPoint[0], worldPoint[1], worldPoint[2]],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
    });
    return local.position;
  }
  objectMatrix(obj, _m).invert();
  _v.set(worldPoint[0], worldPoint[1], worldPoint[2]).applyMatrix4(_m);
  return [_v.x, _v.y, _v.z];
}

/** Convert a local-space point on `obj` to world space. */
export function objectLocalToWorld(
  obj: SceneObject,
  localPoint: readonly [number, number, number],
  parentTransform?: GroupTransform | null,
): [number, number, number] {
  if (parentTransform) {
    return composeGroupTransform(parentTransform, {
      position: [localPoint[0], localPoint[1], localPoint[2]],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
    }).position;
  }
  objectMatrix(obj, _m);
  _v.set(localPoint[0], localPoint[1], localPoint[2]).applyMatrix4(_m);
  return [_v.x, _v.y, _v.z];
}

// ---------------------------------------------------------------------------
// Snap Cursor To — compute a new cursor position from selection / world
// ---------------------------------------------------------------------------

/** Snap a point to the nearest integer unit on each axis. Matches Blender's grid snap. */
export function snapCursorToGridPoint(
  p: readonly [number, number, number],
): [number, number, number] {
  return [Math.round(p[0]), Math.round(p[1]), Math.round(p[2])];
}

/** Median of a set of world-space points. Returns null if none. */
export function medianPoint(
  points: ReadonlyArray<readonly [number, number, number]>,
): [number, number, number] | null {
  if (points.length === 0) return null;
  let x = 0,
    y = 0,
    z = 0;
  for (const p of points) {
    x += p[0];
    y += p[1];
    z += p[2];
  }
  const n = points.length;
  return [x / n, y / n, z / n];
}

/**
 * World-space median of selected vertices on a mesh object. Used by
 * "Snap Cursor To Selection" when in edit mode.
 */
export function editSelectionWorldMedian(
  obj: SceneObject,
  em: EditModeState,
  parentTransform?: GroupTransform | null,
): [number, number, number] | null {
  if (em.selectedVertices.size === 0) return null;
  let local: [number, number, number];
  if (em.isCurve && em.curveEditVertices) {
    const verts = em.curveEditVertices;
    let lx = 0,
      ly = 0,
      lz = 0;
    for (const vi of em.selectedVertices) {
      const pi = Math.floor(vi / 3);
      const comp = vi % 3;
      const base = pi * 9 + comp * 3;
      lx += verts[base];
      ly += verts[base + 1];
      lz += verts[base + 2];
    }
    const n = em.selectedVertices.size;
    local = [lx / n, ly / n, lz / n];
  } else {
    const centroid = vertexCentroid(obj.vertices, em.selectedVertices);
    if (!centroid) return null;
    local = centroid;
  }
  return objectLocalToWorld(obj, local, parentTransform);
}

/**
 * World-space median of selected face centroids. Edit face mode only.
 */
export function editFaceSelectionWorldMedian(
  obj: SceneObject,
  em: EditModeState,
  parentTransform?: GroupTransform | null,
): [number, number, number] | null {
  if (!hasFaceSelection(em)) return null;
  const topo: PolygonTopology = obj.topology;
  let fx = 0,
    fy = 0,
    fz = 0;
  let faceCount = 0;
  for (const f of em.selectedFaces) {
    const faceCentroid = vertexCentroid(
      obj.vertices,
      topologyFaceVerts(topo, f),
    );
    if (!faceCentroid) continue;
    fx += faceCentroid[0];
    fy += faceCentroid[1];
    fz += faceCentroid[2];
    faceCount++;
  }
  if (faceCount === 0) return null;
  return objectLocalToWorld(
    obj,
    [fx / faceCount, fy / faceCount, fz / faceCount],
    parentTransform,
  );
}

// ---------------------------------------------------------------------------
// Snap Selection To Cursor — produce updated scene objects
// ---------------------------------------------------------------------------

/**
 * Move each object in `selectedObjectIds` so its position equals the
 * cursor world position. Returns an updated map of only the affected
 * objects (callers merge into the full map).
 */
export function snapObjectsToCursor(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  selectedObjectIds: ReadonlySet<string>,
  cursorPos: readonly [number, number, number],
): Map<string, SceneObject> {
  const patched = new Map<string, SceneObject>();
  for (const id of selectedObjectIds) {
    const obj = sceneObjects.get(id);
    if (!obj) continue;
    patched.set(id, {
      ...obj,
      position: [cursorPos[0], cursorPos[1], cursorPos[2]],
    });
  }
  return patched;
}

/**
 * Translate each object so the selection's median hits the cursor, while
 * preserving each object's offset from the median — "Selection To Cursor
 * (Offset)" in Blender terms.
 */
export function snapObjectsToCursorOffset(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  selectedObjectIds: ReadonlySet<string>,
  cursorPos: readonly [number, number, number],
): Map<string, SceneObject> {
  const patched = new Map<string, SceneObject>();
  const positions: Array<[string, [number, number, number]]> = [];
  for (const id of selectedObjectIds) {
    const obj = sceneObjects.get(id);
    if (!obj) continue;
    positions.push([id, [obj.position[0], obj.position[1], obj.position[2]]]);
  }
  if (positions.length === 0) return patched;

  const median = medianPoint(positions.map(([, p]) => p));
  if (!median) return patched;
  const dx = cursorPos[0] - median[0];
  const dy = cursorPos[1] - median[1];
  const dz = cursorPos[2] - median[2];
  for (const [id, p] of positions) {
    const obj = sceneObjects.get(id)!;
    patched.set(id, {
      ...obj,
      position: [p[0] + dx, p[1] + dy, p[2] + dz],
    });
  }
  return patched;
}

/**
 * Vertex mode: overwrite each selected vertex with the cursor position
 * (converted to object-local space). Topology is unchanged so normals
 * are recomputed in place.
 */
export function snapVerticesToCursor(
  obj: SceneObject,
  em: EditModeState,
  cursorPos: readonly [number, number, number],
  parentTransform?: GroupTransform | null,
): SceneObject {
  if (em.selectedVertices.size === 0) return obj;
  const local = worldToObjectLocal(obj, cursorPos, parentTransform);
  const newVerts = new Float32Array(obj.vertices);
  for (const vi of em.selectedVertices) {
    newVerts[vi * 3] = local[0];
    newVerts[vi * 3 + 1] = local[1];
    newVerts[vi * 3 + 2] = local[2];
  }
  const loopNormals = recomputeLoopNormals(
    newVerts,
    obj.topology,
    resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
  );
  return {
    ...obj,
    vertices: newVerts,
    loopNormals: asLoopNormals(loopNormals),
  };
}

/**
 * Edge/face mode, or "Selection (Offset)" in vertex mode: translate every
 * selected vertex by the same delta so the selection median hits the
 * cursor. Preserves shape.
 */
export function snapSelectionMedianToCursor(
  obj: SceneObject,
  em: EditModeState,
  cursorPos: readonly [number, number, number],
  parentTransform?: GroupTransform | null,
): SceneObject {
  // Collect affected vertex indices from whichever granularity is live.
  const affected = new Set<number>();
  if (isFaceMode(em)) {
    for (const f of em.selectedFaces) {
      for (const vi of topologyFaceVerts(obj.topology, f)) affected.add(vi);
    }
  } else if (em.granularity === "edge" && hasEdgeSelection(em)) {
    const edges = obj.topology.edges;
    for (const ei of em.selectedEdges) {
      affected.add(edges[ei * 2]);
      affected.add(edges[ei * 2 + 1]);
    }
  } else {
    for (const vi of em.selectedVertices) affected.add(vi);
  }
  if (affected.size === 0) return obj;

  // Compute current median in local space, then delta to cursor-local.
  let mx = 0,
    my = 0,
    mz = 0;
  for (const vi of affected) {
    mx += obj.vertices[vi * 3];
    my += obj.vertices[vi * 3 + 1];
    mz += obj.vertices[vi * 3 + 2];
  }
  const n = affected.size;
  mx /= n;
  my /= n;
  mz /= n;
  const localCursor = worldToObjectLocal(obj, cursorPos, parentTransform);
  const dx = localCursor[0] - mx;
  const dy = localCursor[1] - my;
  const dz = localCursor[2] - mz;

  const newVerts = new Float32Array(obj.vertices);
  for (const vi of affected) {
    newVerts[vi * 3] = obj.vertices[vi * 3] + dx;
    newVerts[vi * 3 + 1] = obj.vertices[vi * 3 + 1] + dy;
    newVerts[vi * 3 + 2] = obj.vertices[vi * 3 + 2] + dz;
  }
  const loopNormals = recomputeLoopNormals(
    newVerts,
    obj.topology,
    resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
  );
  return {
    ...obj,
    vertices: newVerts,
    loopNormals: asLoopNormals(loopNormals),
  };
}

// Re-export for callers that only want types
export type { Cursor3DState };
